/**
 * Los ajustes de esta clase, cambiados con la clase delante.
 *
 * Están en la sala y no en el editor del cuestionario por dos razones, y la segunda pesa más que
 * la primera. Una: el profesorado decide estas cosas **cuando ve quién ha entrado**, no un martes
 * preparando material. Y otra: hoy los ajustes de un cuestionario solo existen si el cuestionario
 * vino de un archivo JSON —la interfaz no escribe ninguno—, así que un interruptor puesto ahí
 * habría nacido apoyado en una cascada que en la práctica está siempre vacía.
 *
 * El barajado es el de las **opciones**, y hay que ser preciso con lo que hace, porque es fácil
 * prometer de más: reparte las cuatro respuestas en un orden distinto **en cada pregunta**, no
 * uno distinto por alumno.
 *
 * Y no es una limitación que convenga quitar. El proyector enseña las cuatro opciones a toda la
 * clase a la vez, y el profesorado dice «la B» en voz alta: si cada móvil tuviera su propio
 * orden, «la B» dejaría de significar lo mismo para dos personas sentadas juntas. Lo que el
 * barajado evita —que la respuesta correcta caiga siempre en el mismo sitio y se copie de la
 * pantalla del de al lado— lo evita igual con un orden por pregunta.
 *
 * **El filtro por dominio no expulsa a nadie que ya esté dentro.** Endurecerlo a mitad de sala
 * cierra la puerta a quien todavía no ha entrado, y nada más: si además reevaluara a los de
 * dentro, la clase se vaciaría por goteo según a quién se le cayera la wifi, porque reconectar
 * pasa por la misma puerta que entrar.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_ROOM_DOMAINS, roomDomainSchema, type RoomSettings } from '@clica/shared';
import { Boton } from './sistema/boton.js';
import { Glifo } from './sistema/glifo.js';
import { Rotulo } from './sistema/rotulo.js';
import { useAvisos } from './sistema/avisos.js';

export function AjustesDeSala({
  settings,
  onChange,
}: {
  settings: RoomSettings;
  onChange: (settings: RoomSettings) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const avisos = useAvisos();
  const [borrador, setBorrador] = useState('');

  // Si los ajustes cambian por otra vía —otra pestaña del profesor, una reconexión— el campo a
  // medio escribir no se pisa, pero la lista sí se refresca sola porque viene de `settings`.
  useEffect(() => setBorrador(''), [settings.allowedDomains.length]);

  function anadirDominio(): void {
    const analizado = roomDomainSchema.safeParse(borrador);
    if (!analizado.success) {
      avisos.fallo(t('game.host.domainInvalid'));
      return;
    }
    if (settings.allowedDomains.includes(analizado.data)) {
      setBorrador('');
      return;
    }
    if (settings.allowedDomains.length >= MAX_ROOM_DOMAINS) {
      avisos.fallo(t('game.host.domainTooMany', { max: MAX_ROOM_DOMAINS }));
      return;
    }
    onChange({ ...settings, allowedDomains: [...settings.allowedDomains, analizado.data] });
    setBorrador('');
  }

  return (
    <section
      aria-labelledby="ajustes-titulo"
      className="rounded-card border-line bg-surface mt-8 border-2 p-5"
    >
      <h2 id="ajustes-titulo" className="font-display text-h3 flex items-center gap-2 font-bold">
        <Glifo nombre="ajustes" size={22} />
        {t('game.host.settingsTitle')}
      </h2>

      <div className="mt-5 flex flex-col gap-5">
        <Interruptor
          id="ajuste-apodos"
          activo={settings.allowAlias}
          onChange={(activo) => onChange({ ...settings, allowAlias: activo })}
          titulo={t('game.host.aliasOn')}
          pie={t('game.host.aliasToggleHelp')}
        />

        {/*
         * Tres estados y no dos: encendido, apagado y «lo que diga el cuestionario».
         *
         * Con dos, no habría forma de volver a heredar sin saber qué traía el cuestionario, y
         * heredar es el valor con el que nace la sala.
         */}
        <fieldset>
          <legend className="font-display font-bold">{t('game.host.shuffleTitle')}</legend>
          <p className="text-micro text-text-dim mt-1">{t('game.host.shuffleHelp')}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {(
              [
                { valor: null, clave: 'game.host.shuffleInherit' },
                { valor: true, clave: 'game.host.shuffleOn' },
                { valor: false, clave: 'game.host.shuffleOff' },
              ] as const
            ).map((opcion) => {
              const elegida = settings.shuffleOptions === opcion.valor;
              return (
                <button
                  key={String(opcion.valor)}
                  type="button"
                  // `aria-pressed` y no solo el estilo: nada se comunica solo por color.
                  aria-pressed={elegida}
                  onClick={() => onChange({ ...settings, shuffleOptions: opcion.valor })}
                  className={[
                    'rounded-chip font-display ease-hard flex min-h-11 items-center gap-2 px-4 font-bold',
                    'border-2 transition-colors duration-(--dur-state)',
                    elegida ? 'border-edge bg-hivis text-on-hivis' : 'border-line hover:bg-ground',
                  ].join(' ')}
                >
                  {elegida ? <Glifo nombre="correcto" size={16} /> : null}
                  {t(opcion.clave)}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div>
          <Rotulo htmlFor="dominio">{t('game.host.domainsTitle')}</Rotulo>
          <p className="text-micro text-text-dim mt-1">
            {settings.allowedDomains.length === 0
              ? t('game.host.domainsEmpty')
              : t('game.host.domainsHelp')}
          </p>

          {settings.allowedDomains.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-2">
              {settings.allowedDomains.map((dominio) => (
                <li
                  key={dominio}
                  className="rounded-chip border-edge bg-ground flex items-center gap-2 border-2 py-1 pr-1 pl-3"
                >
                  <span className="font-display font-bold">{dominio}</span>
                  <button
                    type="button"
                    onClick={() =>
                      onChange({
                        ...settings,
                        allowedDomains: settings.allowedDomains.filter((d) => d !== dominio),
                      })
                    }
                    className="rounded-chip flex min-h-11 min-w-11 items-center justify-center"
                  >
                    <Glifo nombre="cerrar" size={16} />
                    <span className="sr-only">
                      {t('game.host.domainRemove', { domain: dominio })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              id="dominio"
              value={borrador}
              placeholder="institut.cat"
              inputMode="email"
              autoComplete="off"
              onChange={(evento) => setBorrador(evento.target.value)}
              onKeyDown={(evento) => {
                // Enter añade, y no envía nada más: este bloque no vive dentro de un formulario
                // precisamente para que no pueda arrancar la partida sin querer.
                if (evento.key !== 'Enter') return;
                evento.preventDefault();
                anadirDominio();
              }}
              className="text-body rounded-chip border-line bg-ground min-h-11 min-w-0 flex-1 border-2 px-3"
            />
            <Boton variante="secundaria" onClick={anadirDominio}>
              {t('game.host.domainAdd')}
            </Boton>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Un interruptor con su etiqueta y su explicación, que es como se entiende lo que hace. */
function Interruptor({
  id,
  activo,
  onChange,
  titulo,
  pie,
}: {
  id: string;
  activo: boolean;
  onChange: (activo: boolean) => void;
  titulo: string;
  pie: string;
}): React.JSX.Element {
  return (
    <div>
      <label htmlFor={id} className="flex min-h-11 cursor-pointer items-center gap-3">
        {/*
         * `accent-ok` y no `accent-hivis`.
         *
         * El acento sobre papel da 1,16:1, y la marca de verificación que dibuja el navegador es
         * blanca: sobre ese amarillo daría 1,29:1. Una casilla marcada y una vacía se verían
         * igual, que es exactamente lo contrario de lo que una casilla tiene que hacer.
         */}
        <input
          id={id}
          type="checkbox"
          checked={activo}
          onChange={(evento) => onChange(evento.target.checked)}
          className="accent-ok size-6"
        />
        <span className="font-display font-bold">{titulo}</span>
      </label>
      <p className="text-micro text-text-dim mt-1">{pie}</p>
    </div>
  );
}
